type MeetingLike = { meetingId: string; startedAt: string };
type MeetingWithRange = { meetingId: string; startedAt: string; endedAt: string };

/** Picks the meeting to show: `selectedMeetingId` if it's still in the current list,
 *  otherwise the earliest meeting by `startedAt`. A date/period change naturally produces a
 *  meetings list where the old id no longer matches, so selection resets for free. */
export function getEffectiveMeetingId(meetings: MeetingLike[], selectedMeetingId?: string | null): string | undefined {
  if (meetings.length === 0) return undefined;
  if (selectedMeetingId && meetings.some((m) => m.meetingId === selectedMeetingId)) {
    return selectedMeetingId;
  }
  return sortByStartedAt(meetings)[0]?.meetingId;
}

function sortByStartedAt<T extends MeetingLike>(meetings: T[]): T[] {
  return [...meetings].sort((a, b) => a.startedAt.localeCompare(b.startedAt));
}

function getLocalHour(isoString: string, timeZone?: string): number {
  const formatted = new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', hour12: false }).format(
    new Date(isoString),
  );
  // Some environments format midnight as "24" with hour12:false — normalize to 0-23.
  return parseInt(formatted, 10) % 24;
}

/** Derives a display title per meeting: one AM + one PM meeting → "AM"/"PM"; multiple in
 *  the same half-day → "AM 1"/"AM 2" (start order). */
export function getMeetingTitles(meetings: MeetingLike[], timeZone?: string): Map<string, string> {
  const sorted = sortByStartedAt(meetings);
  const am = sorted.filter((m) => getLocalHour(m.startedAt, timeZone) < 12);
  const pm = sorted.filter((m) => getLocalHour(m.startedAt, timeZone) >= 12);

  const titles = new Map<string, string>();
  const assign = (bucket: MeetingLike[], label: string) => {
    bucket.forEach((meeting, index) => {
      titles.set(meeting.meetingId, bucket.length === 1 ? label : `${label} ${index + 1}`);
    });
  };
  assign(am, 'AM');
  assign(pm, 'PM');
  return titles;
}

function formatLocalTime(isoString: string, timeZone?: string): string {
  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(isoString));
  // "6:00 AM" -> "6:00" — no am/pm suffix, since the meeting's AM/PM title label already
  // conveys that.
  return formatted.replace(/\s*[AP]M$/i, '');
}

/** Derives a "6:00 - 7:00" local start/end time range per meeting. */
export function getMeetingTimeRanges(meetings: MeetingWithRange[], timeZone?: string): Map<string, string> {
  const ranges = new Map<string, string>();
  meetings.forEach((meeting) => {
    ranges.set(
      meeting.meetingId,
      `${formatLocalTime(meeting.startedAt, timeZone)} - ${formatLocalTime(meeting.endedAt, timeZone)}`,
    );
  });
  return ranges;
}
